import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "submit", "errorMessage"]
  static values = {
    duplicateTitle: { type: String, default: "Duplicate variant selected" },
    duplicateMessage: { type: String, default: "%{count} duplicate variant(s) detected. Please remove duplicates before saving." }
  }

  connect() {
    this.performCheck()
  }

  checkDuplicates(event) {
    // Use setTimeout to allow DOM to update after add/remove operations
    setTimeout(() => this.performCheck(), 10)
  }

  performCheck() {
    const selects = this.getVisibleSelects()
    this.updateSelectableOptions(selects)
    const selectedValues = new Map() // variant_id -> [select elements]

    // Group selects by their selected value
    selects.forEach(select => {
      const value = select.value
      if (value && value !== "") {
        if (!selectedValues.has(value)) {
          selectedValues.set(value, [])
        }
        selectedValues.get(value).push(select)
      }
    })

    // Find duplicates and track all duplicate selects
    const duplicateSelects = new Set()
    selectedValues.forEach((selectList, value) => {
      if (selectList.length > 1) {
        selectList.forEach(select => duplicateSelects.add(select))
      }
    })

    // Update styling for all selects
    selects.forEach(select => {
      const row = select.closest('.nested-fields')
      if (!row) return

      if (duplicateSelects.has(select)) {
        // Mark as duplicate
        select.classList.add('border-red-500', 'dark:border-red-500', 'bg-red-50', 'dark:bg-red-900/20')
        select.classList.remove('border-slate-300', 'dark:border-slate-600')
        row.classList.add('bg-red-50', 'dark:bg-red-900/10')

        // Add or update warning icon
        this.addWarningIcon(select)
      } else {
        // Remove duplicate styling
        select.classList.remove('border-red-500', 'dark:border-red-500', 'bg-red-50', 'dark:bg-red-900/20')
        select.classList.add('border-slate-300', 'dark:border-slate-600')
        row.classList.remove('bg-red-50', 'dark:bg-red-900/10')

        // Remove warning icon
        this.removeWarningIcon(select)
      }
    })

    // Update submit button and error message
    const hasDuplicates = duplicateSelects.size > 0
    this.updateSubmitButton(hasDuplicates)
    this.updateErrorMessage(hasDuplicates, duplicateSelects.size / 2)
  }

  updateSelectableOptions(selects) {
    const selectedValues = selects
      .map(select => select.value)
      .filter(value => value && value !== "")

    selects.forEach(select => {
      const currentValue = select.value
      const blockedValues = new Set(selectedValues.filter(value => value !== currentValue))

      if (select.tomselect) {
        this.updateTomSelectOptions(select, blockedValues, currentValue)
        return
      }

      Array.from(select.options).forEach(option => {
        if (!option.value) return

        const shouldHide = blockedValues.has(option.value)
        option.disabled = shouldHide
        option.hidden = shouldHide
      })
    })
  }

  updateTomSelectOptions(select, blockedValues, currentValue) {
    const tom = select.tomselect
    if (!tom) return

    Object.entries(tom.options).forEach(([value, option]) => {
      option.disabled = blockedValues.has(value) && value !== currentValue
    })

    tom.clearCache()
    tom.lastQuery = null
    tom.currentResults = null
    tom.refreshOptions(false)
  }

  getVisibleSelects() {
    const allSelects = this.containerTarget.querySelectorAll('select[name*="product_variant_id"]')
    return Array.from(allSelects).filter(select => {
      // Skip selects inside template elements
      if (select.closest('template')) return false
      
      // Check if the wrapper is actually visible (checks computed style for Tailwind classes)
      const wrapper = select.closest('[class*="hidden"],[class*="block"]') || select.parentElement
      if (wrapper && window.getComputedStyle(wrapper).display === 'none') {
        return false
      }
      
      const row = select.closest('.nested-fields')
      if (!row) return false

      const destroyInput = row.querySelector('input[name*="_destroy"]')
      return row.style.display !== 'none' && (!destroyInput || destroyInput.value !== '1')
    })
  }

  addWarningIcon(select) {
    const wrapper = select.parentElement
    if (wrapper.querySelector('.duplicate-warning')) return

    const icon = document.createElement('div')
    icon.className = 'duplicate-warning absolute right-8 top-1/2 -translate-y-1/2 text-red-500'
    icon.innerHTML = `
      <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
      </svg>
    `
    icon.title = this.duplicateTitleValue

    // Make wrapper relative if not already
    if (!wrapper.classList.contains('relative')) {
      wrapper.classList.add('relative')
    }

    wrapper.appendChild(icon)
  }

  removeWarningIcon(select) {
    const wrapper = select.parentElement
    const icon = wrapper.querySelector('.duplicate-warning')
    if (icon) {
      icon.remove()
    }
  }

  updateSubmitButton(hasDuplicates) {
    if (!this.hasSubmitTarget) return

    if (hasDuplicates) {
      this.submitTarget.disabled = true
      this.submitTarget.classList.add('opacity-50', 'cursor-not-allowed')
      this.submitTarget.classList.remove('hover:bg-teal-700')
    } else {
      this.submitTarget.disabled = false
      this.submitTarget.classList.remove('opacity-50', 'cursor-not-allowed')
      this.submitTarget.classList.add('hover:bg-teal-700')
    }
  }

  updateErrorMessage(hasDuplicates, duplicateCount) {
    if (!this.hasErrorMessageTarget) return

    if (hasDuplicates) {
      this.errorMessageTarget.textContent = `⚠️ ${this.duplicateMessageValue.replace('%{count}', Math.floor(duplicateCount))}`
      this.errorMessageTarget.classList.remove('hidden')
    } else {
      this.errorMessageTarget.classList.add('hidden')
    }
  }
}
